import { readNavigationMarker } from "./navigation-marker-value";
import { budgets, report, type Visit } from "./navigation-readiness-policy";

type VisitOptions = {
  readonly record: Visit;
  readonly release: string;
  readonly onDeadline: () => void;
};

export class NavigationVisit {
  readonly record: Visit;
  private readonly release: string;
  private readonly onDeadline: () => void;
  private timers: ReturnType<typeof setTimeout>[] = [];
  private stopped = false;

  constructor({ record, release, onDeadline }: VisitOptions) {
    this.record = record;
    this.release = release;
    this.onDeadline = onDeadline;
  }

  start(): void {
    this.setDeadlines();
    report({
      current: this.record,
      stage: "shell",
      status: "started",
      release: this.release,
    });
  }

  redirect(target: Pick<Visit, "path" | "search" | "family" | "stages">): void {
    Object.assign(this.record, target, { redirected: true });
    this.setDeadlines();
  }

  finish(): void {
    this.stopped = true;

    for (const timer of this.timers) clearTimeout(timer);

    for (const stage of this.record.stages) {
      if (!this.record.settled.has(stage))
        report({
          current: this.record,
          stage,
          status: "abandoned",
          release: this.release,
        });
    }
  }

  settle(entry: {
    readonly marker: HTMLElement;
    readonly hydrated: boolean;
  }): void {
    const current = this.record;
    const marker = readNavigationMarker({ current, ...entry });

    if (!marker) return;

    const { stage, status } = marker;
    const budget = budgets[current.load === "cold" ? "cold" : "soft"][stage];

    if (
      performance.now() - current.started >= budget &&
      !current.overdue.has(stage)
    ) {
      current.overdue.add(stage);
      report({ current, stage, status: "timeout", release: this.release });
    }

    current.settled.add(stage);
    report({ current, stage, status, release: this.release });
  }

  private setDeadlines(): void {
    for (const timer of this.timers) clearTimeout(timer);

    const current = this.record;
    this.timers = current.stages.map((stage) => {
      const budget = budgets[current.load === "cold" ? "cold" : "soft"][stage];
      return setTimeout(
        () => {
          this.onDeadline();

          if (
            this.stopped ||
            current.settled.has(stage) ||
            current.overdue.has(stage)
          )
            return;

          current.overdue.add(stage);
          report({ current, stage, status: "timeout", release: this.release });
        },
        Math.max(0, budget - (performance.now() - current.started)),
      );
    });
  }
}
